import { ChartCardModal } from '@backrs_ui/common-ui/dist';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  ProfileValueOption,
  ProfileFields,
  UserProfileResponse,
  ProfileValueOptionResponse,
} from '@backrs_ui/grow-ui/src/redux/profile/profile';

import { ResolverFunction } from './resolvers.config';
import {
  FieldConfig,
  ProfilePageConfig,
  AcademicApTestValue,
  AcademicEntranceExamValue,
  Validation,
  AcademicMajorValue,
} from '../types/profile.types';
import { createFieldConfig } from '../utils/fields.helpers';
import { getEnrollmentIds, getGraduationYears } from '../utils/options.helpers';
import {
  mapStandardProfileArrayValues,
  mapStandardProfileSingleValue,
} from '../utils/resolvers.helpers';

const ENTRANCE_EXAM_SCORE_RANGES: Record<
  string,
  { min: number; max: number; format?: 'decimal' | 'whole' }
> = {
  GRE: { min: 130, max: 170, format: 'whole' },
  GMAT: { min: 200, max: 800, format: 'whole' },
  LSAT: { min: 120, max: 180, format: 'whole' },
  MCAT: { min: 472, max: 528, format: 'whole' },
  TOEFL: { min: 0, max: 120, format: 'whole' },
  IELTS: { min: 1, max: 9, format: 'decimal' },
};

export const ACADEMIC_FIELD_CONFIGS: Partial<FieldConfig> = {
  academicsHighSchoolCurrentEnrollment: createFieldConfig({
    key: 'academicsHighSchoolCurrentEnrollment',
    type: { variant: 'API_AUTOCOMPLETE', autoCompleteEndpoint: '/profile/lookups/schools' },
    maxSelectedOptions: 1,
    placeholder: 'Search Schools',
  }),
  academicsPostSecondaryCurrentEnrollment: createFieldConfig({
    key: 'academicsPostSecondaryCurrentEnrollment',
    type: { variant: 'API_AUTOCOMPLETE', autoCompleteEndpoint: '/profile/lookups/schools' },
    maxSelectedOptions: 1,
    placeholder: 'Search Schools',
    disabledBy: {
      field: 'academicEnrollmentTypes',
      values: (options?: ProfileValueOptionResponse) =>
        getEnrollmentIds(options, 'high_school').map((id) => id.toString()),
    },
  }),
  academicsAlmaMaters: createFieldConfig({
    key: 'academicsAlmaMaters',
    type: { variant: 'API_AUTOCOMPLETE', autoCompleteEndpoint: '/profile/lookups/schools' },
    placeholder: 'Search Schools',
    showSearch: true,
    disabledBy: {
      field: 'academicEnrollmentTypes',
      values: (options?: ProfileValueOptionResponse) =>
        getEnrollmentIds(options, 'high_school').map((id) => id.toString()),
    },
  }),
  academicEnrollmentTypes: createFieldConfig({
    key: 'academicEnrollmentTypes',
    type: { variant: 'SINGLE_SELECT' },
    placeholder: 'Select an Enrollment Type',
  }),
  academicHighSchoolExpectedGraduationYear: createFieldConfig({
    key: 'academicHighSchoolExpectedGraduationYear',
    type: { variant: 'SINGLE_SELECT' },
    placeholder: 'Select a Graduation Year',

    customOptions: ({ profile, options }) => {
      return getGraduationYears('high_school', profile, options);
    },
  }),

  academicPostSecondaryExpectedGraduationYear: createFieldConfig({
    key: 'academicPostSecondaryExpectedGraduationYear',
    type: { variant: 'SINGLE_SELECT' },
    placeholder: 'Select a Graduation Year',
    customOptions: ({ profile, options }) => {
      return getGraduationYears('post_secondary', profile, options);
    },
    disabledBy: {
      field: 'academicEnrollmentTypes',
      values: (options?: ProfileValueOptionResponse) =>
        getEnrollmentIds(options, 'high_school').map((id) => id.toString()),
    },
  }),

  academicDegrees: createFieldConfig({
    key: 'academicDegrees',
    type: { variant: 'SINGLE_SELECT' },
    placeholder: 'Select a Degree',
    disabledBy: {
      field: 'academicEnrollmentTypes',
      values: (options?: ProfileValueOptionResponse) =>
        getEnrollmentIds(options, 'high_school').map((id) => id.toString()),
    },
  }),

  academicMajors: {
    ...createFieldConfig({
      key: 'academicMajors',
      type: { variant: 'AUTOCOMPLETE', manualFilterOptions: true },
      chipType: 'tag',
      inlineChip: true,
    }),
    validation: { type: 'number', format: 'decimal', min: 0.0, max: 4.0, decimals: 2 },
    placeholder: 'Search Academic Majors',
    disabledBy: {
      field: 'academicEnrollmentTypes',
      values: (options?: ProfileValueOptionResponse) =>
        getEnrollmentIds(options, 'high_school').map((id) => id.toString()),
    },
  },

  major_gpa: {
    ...createFieldConfig({
      key: 'major_gpa',
      type: { variant: 'AUTOCOMPLETE', manualFilterOptions: true },
      chipType: 'tag',
      inlineChip: true,
    }),
    createDynamicInput: true,
    dynamicInputConfig: {
      type: 'TEXT',
      width: 'half',
      getDynamicConfig: () => {
        return {
          placeholder: '4.0',
          validation: { type: 'number', format: 'decimal', min: 0.0, max: 4.0, decimals: 2 },
        };
      },
    },
    validation: { type: 'number', format: 'decimal', min: 0.0, max: 4.0, decimals: 2 },
    placeholder: 'Search Academic Majors',
    disabledBy: {
      field: 'academicEnrollmentTypes',
      values: (options?: ProfileValueOptionResponse) =>
        getEnrollmentIds(options, 'high_school').map((id) => id.toString()),
    },
    hideInput: true,
  },

  academicApTests: {
    ...createFieldConfig({
      key: 'academicApTests',
      type: { variant: 'AUTOCOMPLETE', manualFilterOptions: true },
      showSearch: true,
    }),
    createDynamicInput: true,
    dynamicInputConfig: {
      type: 'SINGLE_SELECT',
      width: 'half',
      getDynamicConfig: (selectedOption: ProfileValueOption) => {
        // Determine if this is an AP or IB test
        const isIBTest =
          selectedOption.name.toLowerCase().includes('ib ') ||
          selectedOption.name.toLowerCase().includes('international baccalaureate');

        return {
          options: isIBTest
            ? [
                { label: '1', value: '1' },
                { label: '2', value: '2' },
                { label: '3', value: '3' },
                { label: '4', value: '4' },
                { label: '5', value: '5' },
                { label: '6', value: '6' },
                { label: '7', value: '7' },
              ]
            : [
                { label: '1', value: '1' },
                { label: '2', value: '2' },
                { label: '3', value: '3' },
                { label: '4', value: '4' },
                { label: '5', value: '5' },
              ],
          placeholder: isIBTest ? '7' : '5',
          validation: isIBTest
            ? { type: 'number', format: 'whole', min: 1, max: 7 }
            : { type: 'number', format: 'whole', min: 1, max: 5 },
        };
      },
    },
    validation: { type: 'number', format: 'whole', min: 1, max: 7 }, // Max range for fallback
    placeholder: 'Search AP/IB Tests',
  },

  academicEntranceExams: {
    ...createFieldConfig({
      key: 'academicEntranceExams',
      type: { variant: 'AUTOCOMPLETE', manualFilterOptions: true },
      showSearch: true,
    }),
    createDynamicInput: true,
    dynamicInputConfig: {
      type: 'TEXT',
      width: 'half',
      // each test has a different score range, so we need to get the score range for the selected test
      getDynamicConfig: (selectedOption: ProfileValueOption) => {
        const name = selectedOption.name.toUpperCase();

        const examKey = Object.keys(ENTRANCE_EXAM_SCORE_RANGES).find((key) => name.startsWith(key));

        const fallbackValidation: Validation = {
          type: 'number',
          format: 'whole',
          min: 0,
          max: 800,
        };

        if (!examKey) {
          return {
            placeholder: 'Enter score',
            validation: fallbackValidation,
          };
        }

        const { min, max, format = 'whole' } = ENTRANCE_EXAM_SCORE_RANGES[examKey];

        return {
          placeholder: `${max}`,
          validation: { type: 'number', format, min, max },
        };
      },
    },
    validation: { type: 'number', format: 'whole', min: 0, max: 800 }, // fallback/global
    placeholder: 'Search Entrance Exams',
    disabledBy: {
      field: 'academicEnrollmentTypes',
      values: (options?: ProfileValueOptionResponse) =>
        getEnrollmentIds(options, 'high_school').map((id) => id.toString()),
    },
  },
  academicRigor: createFieldConfig({
    key: 'academicRigor',
    type: { variant: 'SINGLE_SELECT' },
    placeholder: 'Select Rigor',
  }),
  resourcesClubs: createFieldConfig({
    key: 'resourcesClubs',
    type: { variant: 'API_AUTOCOMPLETE', autoCompleteEndpoint: '/profile/lookups/clubs' },
    chipType: 'tag',
    showSearch: true,
    placeholder: 'Search Clubs',
  }),
  academicExtracurricularInvolvement: createFieldConfig({
    key: 'academicExtracurricularInvolvement',
    type: { variant: 'SINGLE_SELECT' },
    placeholder: 'Select Extracurricular Involvement',
  }),
  academicEducationalExperiences: createFieldConfig({
    key: 'academicEducationalExperiences',
    type: { variant: 'CHECKBOX_GROUP' },
  }),
  high_school_gpa: createFieldConfig({
    key: 'high_school_gpa',
    type: { variant: 'TEXT' },
    validation: { type: 'number', format: 'decimal', min: 0.0, max: 4.0, decimals: 2 },
    placeholder: '4.0',
  }),
  post_secondary_gpa: createFieldConfig({
    key: 'post_secondary_gpa',
    type: { variant: 'TEXT' },
    validation: { type: 'number', format: 'decimal', min: 0.0, max: 4.0, decimals: 2 },
    placeholder: '4.0',
    disabledBy: {
      field: 'academicEnrollmentTypes',
      values: (options?: ProfileValueOptionResponse) =>
        getEnrollmentIds(options, 'high_school').map((id) => id.toString()),
    },
  }),
  sat_score: createFieldConfig({
    key: 'academicMetrics',
    type: { variant: 'TEXT' },
    width: 'half',
    textLeadingAdornment: {
      name: 'SAT',
    },
    validation: { type: 'number', format: 'whole', min: 1, max: 1600 },
    placeholder: '1600',
  }),
  act_score: createFieldConfig({
    key: 'academicMetrics',
    type: { variant: 'TEXT' },
    width: 'half',
    textLeadingAdornment: {
      name: 'ACT',
    },
    validation: { type: 'number', format: 'whole', min: 1, max: 36 },
    placeholder: '36',
  }),
};

export const ACADEMIC_PAGE_CONFIG: ProfilePageConfig = {
  title: 'Academics',
  description:
    "Update your education details here. We'll use to share strong-fit internships, scholarships, guidance, people, and resources.\nPlease include the high school info you know even if you are enrolled in a college or a trade school.",
  sections: [
    {
      title: 'Enrollment Type',
      keys: ['academicEnrollmentTypes'],
    },
    {
      title: 'High School',
      keys: ['academicsHighSchoolCurrentEnrollment'],
      dynamicDescription: (profile: UserProfileResponse, options?: ProfileValueOptionResponse) => {
        const highSchoolIds = getEnrollmentIds(options, 'high_school');

        const value = profile.profile.academicEnrollmentTypes;

        return value && highSchoolIds?.includes(value)
          ? 'Name of your current institution'
          : 'Name of High School';
      },
      hideDivider: false,
    },
    {
      title: 'Expected Graduation Year',
      keys: ['academicHighSchoolExpectedGraduationYear'],
      dynamicTitle: (profile: UserProfileResponse, options?: ProfileValueOptionResponse) => {
        const highSchoolIds = getEnrollmentIds(options, 'high_school');

        const value = profile.profile.academicEnrollmentTypes;

        return value && highSchoolIds?.includes(value)
          ? 'Expected Graduation Year'
          : 'Graduation Year';
      },
      hideDivider: true,
    },
    {
      title: 'High School GPA',
      keys: ['high_school_gpa'],
      hideDivider: true,
    },
    {
      title: 'Standardized Test Scores',
      keys: ['sat_score', 'act_score'],
    },
    {
      title: 'AP/IB Test Scores',
      keys: ['academicApTests'],
      hideDivider: true,
    },

    {
      title: 'Rigor',
      keys: ['academicRigor'],
      description: 'The difficulty of your school coursework.',
      viewExamples: {
        label: 'Learn more',
        content: (isOpen, setIsOpen) => (
          <ChartCardModal
            isModalOpen={isOpen}
            setIsModalOpen={setIsOpen}
            modalData={appStrings.modules.profile.edit.rigor.modal}
          />
        ),
      },
    },
    {
      title: 'Extracurricular Strength',
      keys: ['academicExtracurricularInvolvement'],
      description: 'The extent of activities you engage in outside of the classroom.',
      hideDivider: true,
      viewExamples: {
        label: 'Learn more',
        content: (isOpen, setIsOpen) => (
          <ChartCardModal
            isModalOpen={isOpen}
            setIsModalOpen={setIsOpen}
            modalData={appStrings.modules.college.whoGetsIn.charts.extracurriculars.modal}
          />
        ),
      },
    },

    {
      title: 'Organizations',
      keys: ['resourcesClubs'],
    },

    {
      title: 'School',
      description: 'Name of your current institution',
      dynamicDescription: (profile, options) => {
        const value = profile.profile.academicEnrollmentTypes;

        const postSecondaryIds = getEnrollmentIds(options, 'post_secondary');

        return value && postSecondaryIds?.includes(value)
          ? 'Name of your current institution'
          : 'Post Secondary Institution';
      },
      keys: ['academicsPostSecondaryCurrentEnrollment'],
      hideDivider: true,
    },
    {
      title: 'Expected Graduation Year',
      keys: ['academicPostSecondaryExpectedGraduationYear'],
      dynamicTitle: (profile, options) => {
        const value = profile.profile.academicEnrollmentTypes;

        const postSecondaryIds = getEnrollmentIds(options, 'post_secondary');

        return value && postSecondaryIds?.includes(value)
          ? 'Expected Graduation Year'
          : 'Graduation Year';
      },
      hideDivider: true,
    },
    {
      title: 'GPA',
      keys: ['post_secondary_gpa'],
      hideDivider: true,
    },
    {
      title: 'College Major(s)',
      keys: ['academicMajors'],
    },
    {
      title: 'Major GPA',
      keys: ['major_gpa'],
      hideDivider: true,
    },
    {
      title: 'Entrance Exam Scores',
      keys: ['academicEntranceExams'],
    },
    {
      title: 'Highest Level Degree',
      keys: ['academicDegrees'],
    },
    {
      title: 'Alma Maters',
      keys: ['academicsAlmaMaters'],
      hideDivider: true,
    },
    {
      title: 'Educational Experiences',
      description: 'Select any that apply to you.',
      keys: ['academicEducationalExperiences'],
    },
  ],
};

export const ACADEMIC_HELPERS: Partial<
  Record<ProfileFields, ResolverFunction<string | number | ProfileValueOption>>
> = {
  academicEnrollmentTypes: (profile, options) =>
    mapStandardProfileSingleValue(
      profile.profile.academicEnrollmentTypes,
      options.academicEnrollmentTypes,
    ),

  academicsHighSchoolCurrentEnrollment: (profile) => {
    const value =
      typeof profile.profile.academicsHighSchoolCurrentEnrollment === 'string'
        ? profile.profile.academicsHighSchoolCurrentEnrollment
        : profile.profile.academicsHighSchoolCurrentEnrollment?.school;

    return value ? [value] : [];
  },

  academicsPostSecondaryCurrentEnrollment: (profile) => {
    const value =
      typeof profile.profile.academicsPostSecondaryCurrentEnrollment === 'string'
        ? profile.profile.academicsPostSecondaryCurrentEnrollment
        : profile.profile.academicsPostSecondaryCurrentEnrollment?.school;

    return value ? [value] : [];
  },

  academicHighSchoolExpectedGraduationYear: (profile) => {
    const value = profile.profile.academicHighSchoolExpectedGraduationYear;

    return value ? [value] : [];
  },

  academicPostSecondaryExpectedGraduationYear: (profile) => {
    const value = profile.profile.academicPostSecondaryExpectedGraduationYear;

    return value ? [value] : [];
  },

  academicDegrees: (profile, options) =>
    mapStandardProfileSingleValue(profile.profile.academicDegrees, options.academicDegrees),

  academicsAlmaMaters: (profile) =>
    (profile.profile.academicsAlmaMaters ?? [])
      .map((school) => (typeof school === 'string' ? school : school.school))
      .filter(Boolean),

  academicMajors: (profile, options): AcademicMajorValue[] =>
    (profile.profile.academicMajors ?? [])
      .map((major) => {
        const opt = options.academicMajors.find((opt) => opt.id === major.academic_majors_id);

        return opt ? { ...opt, major_gpa: major.major_gpa } : null;
      })
      .filter((opt): opt is AcademicMajorValue => Boolean(opt)),

  major_gpa: (profile, options): AcademicMajorValue[] =>
    (profile.profile.academicMajors ?? [])
      .map((major) => {
        const opt = options.academicMajors.find((opt) => opt.id === major.academic_majors_id);

        return opt ? { ...opt, major_gpa: major.major_gpa } : null;
      })
      .filter((opt): opt is AcademicMajorValue => Boolean(opt)),

  high_school_gpa: (profile) => {
    const metrics = profile.profile.academicMetrics ?? {};

    return metrics.high_school_gpa ? [metrics.high_school_gpa] : [];
  },

  post_secondary_gpa: (profile) => {
    const metrics = profile.profile.academicMetrics ?? {};

    return metrics.post_secondary_gpa ? [metrics.post_secondary_gpa] : [];
  },

  sat_score: (profile) => {
    const metrics = profile.profile.academicMetrics ?? {};

    return metrics.sat_score ? [metrics.sat_score] : [];
  },

  act_score: (profile) => {
    const metrics = profile.profile.academicMetrics ?? {};

    return metrics.act_score ? [metrics.act_score] : [];
  },

  academicApTests: (profile, options): AcademicApTestValue[] =>
    (profile.profile.academicApTests ?? [])
      .map((test) => {
        const opt = options.academicApTests.find((opt) => opt.id === test.ap_test_id);

        return opt ? { ...opt, score: test.score } : null;
      })
      .filter((opt): opt is AcademicApTestValue => Boolean(opt)),

  academicEntranceExams: (profile, options): AcademicEntranceExamValue[] =>
    (profile.profile.academicEntranceExams ?? [])
      .map((test) => {
        const opt = options.academicEntranceExams.find((opt) => opt.id === test.entrance_exam_id);

        return opt ? { ...opt, score: test.score } : null;
      })
      .filter((opt): opt is AcademicApTestValue => Boolean(opt)),

  academicRigor: (profile, options) =>
    mapStandardProfileSingleValue(profile.profile.academicRigor, options.academicRigor),

  academicExtracurricularInvolvement: (profile, options) =>
    mapStandardProfileSingleValue(
      profile.profile.academicExtracurricularInvolvement,
      options.academicExtracurricularInvolvement,
    ),

  resourcesClubs: (profile) => (profile.profile.resourcesClubs ?? []).filter(Boolean),

  academicEducationalExperiences: (profile, options) =>
    mapStandardProfileArrayValues(
      profile.profile.academicEducationalExperiences,
      options.academicEducationalExperiences,
    ),
};
